import type { AnyRouter, LinkOptions, RegisteredRouter, RoutePaths, UseLinkPropsOptions } from "@tanstack/react-router";
import type { PressEvent } from "react-aria-components";

//#region Generic Action type
export type ActionEventFn = (e: PressEvent) => void | Promise<void>;
export type ActionFn = () => void | Promise<void>;

export type RouterLinkProps = LinkOptions;

export interface AnchorProps {
    href: HTMLAnchorElement["href"];
    target?: HTMLAnchorElement["target"];
    rel?: HTMLAnchorElement["rel"];
}

export const DISABLED = Symbol.for("Action/DISABLED");
type ActionDisabled = typeof DISABLED;

/**
 * An action to take on clicking an element.
 *
 * Can be a:
 *  - press handler function
 *  - router link via {@link link()}
 *  - URL string
 *  - link properties as {@link AnchorProps}
 *  - disabled ({@link DISABLED})
 *
 * @example
 *  action={e => console.log("Clicked", e)}
 *  action={link({ to: "/view/$id", params: { id: "1" } })}
 *  action="https://example.com"
 *  action={{ href: "https://example.com", target: "_blank" }}
 */
export type Action = ActionEventFn | UniversalAction;

/**
 * An action that only supports action functions without a press event argument.
 * @see {@link Action}
 */
export type UniversalAction = ActionFn | ActionDisabled | string | RouterLinkProps | AnchorProps;

export function actionIsEvent(action: Action | undefined): action is ActionEventFn {
    return typeof action === "function";
}

export function actionIsRouterLink(action: Action | undefined): action is RouterLinkProps {
    return typeof action === "object" && action != null && !("href" in action);
}

/** NB: Use {@link normalizeAction} to get rid of URL strings from {@link Action} */
function actionIsUrl(action: Action | undefined): action is string {
    return typeof action === "string";
}

export function actionIsLinkProps(action: Action | undefined): action is AnchorProps {
    return typeof action === "object" && action != null && "href" in action;
}

export function actionIsDisabled(action: Action | undefined): action is ActionDisabled {
    return action === DISABLED;
}
//#endregion

//#region Normalize Action into a function or props

/** Convert string Actions into {@link AnchorProps} */
export function normalizeAction<T extends Action | undefined>(action: T) {
    if (actionIsUrl(action)) {
        const link: AnchorProps = { href: action, rel: "noopener" };
        return link;
    }
    return action as Exclude<T, string>;
}
//#endregion

/**
 * Validate a router link as type-safe and return a generic {@link RouterLinkProps}.
 * @example link({ to: "/view/$id", params: { id: "1" } })
 */
export function link<
    /** NB: Generic params copied from {@link UseLinkPropsOptions} */
    TRouter extends AnyRouter = RegisteredRouter,
    TFrom extends RoutePaths<TRouter["routeTree"]> | string = string,
    TTo extends string = "",
    TMaskFrom extends RoutePaths<TRouter["routeTree"]> | string = TFrom,
    TMaskTo extends string = "",
>(options: UseLinkPropsOptions<TRouter, TFrom, TTo, TMaskFrom, TMaskTo>) {
    return options as RouterLinkProps;
}

export function buildHrefFromAction(router: RegisteredRouter, action: RouterLinkProps): string {
    const next = router.buildLocation(action);
    return router.history.createHref(next.href);
}
